import { useEffect, useMemo, useState, useCallback } from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { toast } from "sonner";
import {
  Mail, MessageSquare, Trash2, RefreshCcw, Filter, Circle, CheckCircle2,
} from "lucide-react";
import {
  listContactMessages,
  updateContactMessage,
  deleteContactMessage,
} from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "read", label: "Read" },
];

function timeAgo(iso) {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

export default function AdminMessages() {
  const { admin } = useAuth();
  const isAdmin = admin?.role === "admin";
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [delDlg, setDelDlg] = useState({ open: false, id: null, name: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listContactMessages();
      setMessages(data);
    } catch {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return messages;
    if (filter === "unread") return messages.filter((m) => !m.is_read);
    return messages.filter((m) => m.is_read);
  }, [messages, filter]);

  const counts = useMemo(() => ({
    all: messages.length,
    unread: messages.filter((m) => !m.is_read).length,
    read: messages.filter((m) => m.is_read).length,
  }), [messages]);

  const selected = useMemo(
    () => messages.find((m) => m.id === selectedId) || null,
    [messages, selectedId]
  );

  async function openMessage(msg) {
    setSelectedId(msg.id);
    if (!msg.is_read) {
      try {
        const upd = await updateContactMessage(msg.id, { is_read: true });
        setMessages((ms) => ms.map((m) => (m.id === msg.id ? { ...m, ...upd } : m)));
      } catch {
        // silent
      }
    }
  }

  async function toggleRead(msg) {
    try {
      const upd = await updateContactMessage(msg.id, { is_read: !msg.is_read });
      setMessages((ms) => ms.map((m) => (m.id === msg.id ? { ...m, ...upd } : m)));
    } catch {
      toast.error("Failed to update");
    }
  }

  async function confirmDelete() {
    try {
      await deleteContactMessage(delDlg.id);
      setMessages((ms) => ms.filter((m) => m.id !== delDlg.id));
      if (selectedId === delDlg.id) setSelectedId(null);
      toast.success("Message deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDelDlg({ open: false, id: null, name: "" });
    }
  }

  return (
    <div data-testid="page-admin-messages">
      <main className="mx-auto max-w-[1400px] px-6 lg:px-12 py-14">
        <div className="flex items-end justify-between gap-8 mb-10">
          <div>
            <div className="overline">Messages</div>
            <h1 className="mt-4 serif text-4xl md:text-5xl font-light leading-tight text-foreground">
              Contact enquiries.
            </h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl">
              Press, events, general enquiries. Click to read — messages are auto-marked as read.
            </p>
          </div>
          <button
            data-testid="admin-messages-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-border text-[0.72rem] uppercase tracking-[0.22em] text-foreground/85 hover:border-primary hover:text-primary transition-colors"
          >
            <RefreshCcw size={12} strokeWidth={1.5} />
            Refresh
          </button>
        </div>

        <div className="mb-8 flex flex-wrap items-center gap-2 border-y border-border py-4">
          <Filter size={12} strokeWidth={1.5} className="text-muted-foreground mr-2" />
          {FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                data-testid={`admin-messages-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-[0.68rem] uppercase tracking-[0.2em] border transition-colors ${
                  active
                    ? "border-primary text-primary bg-primary/10"
                    : "border-border text-foreground/70 hover:text-foreground hover:border-foreground/50"
                }`}
              >
                {f.label}
                <span className="text-muted-foreground tabular-nums">{counts[f.key]}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div data-testid="messages-loading" className="py-24 text-center text-muted-foreground italic serif text-xl">
            <span className="ornament">Loading messages</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center">
            <MessageSquare size={28} className="mx-auto text-muted-foreground" />
            <p className="mt-4 text-muted-foreground">
              {filter === "all" ? "No messages yet." : `No ${filter} messages.`}
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5 space-y-2">
              {filtered.map((m) => {
                const active = selectedId === m.id;
                return (
                  <button
                    key={m.id}
                    data-testid={`admin-message-row-${m.id}`}
                    onClick={() => openMessage(m)}
                    className={`w-full text-left border p-4 transition-all duration-200 bg-card ${
                      active
                        ? "border-primary shadow-[0_0_0_1px_hsl(var(--primary))]"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1 shrink-0">
                        {m.is_read ? (
                          <Circle size={9} strokeWidth={1.5} className="text-muted-foreground/60" />
                        ) : (
                          <CheckCircle2 size={9} strokeWidth={1.5} className="text-primary fill-primary" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-3">
                          <div className={`truncate ${m.is_read ? "text-foreground/80" : "text-foreground font-medium"}`}>
                            {m.name}
                          </div>
                          <div className="text-[0.65rem] uppercase tracking-[0.18em] text-muted-foreground shrink-0">
                            {timeAgo(m.created_at)}
                          </div>
                        </div>
                        <div className={`text-sm mt-1 truncate ${m.is_read ? "text-muted-foreground" : "text-foreground/90"}`}>
                          {m.subject}
                        </div>
                        <div className="text-xs mt-1 text-muted-foreground truncate">
                          {m.message}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <aside className="lg:col-span-7">
              <div className="sticky top-40 border border-border bg-card p-8">
                {!selected ? (
                  <div className="py-16 text-center text-muted-foreground text-sm">
                    Select a message to read.
                  </div>
                ) : (
                  <div data-testid="message-detail" className="fade-up">
                    <div className="overline">{selected.is_read ? "Read" : "Unread"}</div>
                    <h2 className="mt-3 serif text-3xl font-light text-foreground leading-tight">
                      {selected.subject}
                    </h2>
                    <div className="mt-3 text-sm text-muted-foreground">
                      From <span className="text-foreground">{selected.name}</span> ·{" "}
                      <a href={`mailto:${selected.email}`} className="hover:text-primary transition-colors">
                        {selected.email}
                      </a>
                      {" · "}
                      {timeAgo(selected.created_at)}
                    </div>

                    <div className="mt-8 pt-8 border-t border-border text-sm text-foreground/90 leading-relaxed whitespace-pre-line">
                      {selected.message}
                    </div>

                    <div className="mt-10 flex flex-wrap gap-2">
                      <a
                        href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                        data-testid="message-reply"
                        className="inline-flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground text-[0.7rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 transition-colors"
                      >
                        <Mail size={12} strokeWidth={1.5} /> Reply by Email
                      </a>
                      <button
                        data-testid="message-toggle-read"
                        onClick={() => toggleRead(selected)}
                        className="inline-flex items-center gap-2 px-4 py-3 border border-border text-[0.7rem] uppercase tracking-[0.22em] text-foreground/85 hover:border-primary hover:text-primary transition-colors"
                      >
                        Mark as {selected.is_read ? "Unread" : "Read"}
                      </button>
                      {isAdmin && (
                        <button
                          data-testid="message-delete"
                          onClick={() => setDelDlg({ open: true, id: selected.id, name: selected.name })}
                          className="ml-auto inline-flex items-center gap-2 px-3 py-3 text-muted-foreground hover:text-destructive transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={13} strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        )}
      </main>

      <AlertDialog
        open={delDlg.open}
        onOpenChange={(v) => setDelDlg((s) => ({ ...s, open: v }))}
      >
        <AlertDialogContent
          data-testid="message-delete-dialog"
          className="bg-card border border-border rounded-none text-foreground"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="serif text-2xl font-light">
              Delete message?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground text-sm">
              Permanently remove the message from {delDlg.name}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-border bg-transparent hover:bg-accent text-foreground/80">
              Keep
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="message-delete-confirm"
              className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
